import { PayloadAction } from '@reduxjs/toolkit'
import {
  editAlias as editAliasService,
  editCashAccountsAlias as editCashAccountsService,
} from '@repo/accounts-services'
import { EditAliasPayload } from '@repo/accounts-types'
import {
  ApiClient,
  ApiResponse,
  Domain,
  GenericOperationState,
  GlobalError,
  Service,
} from '@repo/core'
import { BankingProductType } from '@repo/shared-types'
import { AxiosError, AxiosInstance } from 'axios'
import { call, getContext, put, PutEffect } from 'redux-saga/effects'
import {
  accountDetailDataActions,
  accountListDataActions,
  bankingProductsDataActions,
  editAliasDataActions,
} from '../../../../slices'

interface EditAliasActionConfig {
  service: Service<EditAliasPayload, ApiResponse<void>>
  onRequestSuccess?: (
    action: PayloadAction<EditAliasPayload>,
  ) => Generator<PutEffect<{ payload: any; type: `${string}/${string}` }>, void, unknown>
}

const editAliasActionByProductType: Partial<Record<BankingProductType, EditAliasActionConfig>> = {
  CASH: {
    service: editCashAccountsService,
    onRequestSuccess: function* (action: PayloadAction<EditAliasPayload>) {
      yield put(bankingProductsDataActions.updateAlias(action.payload))
    },
  },
  INVESTMENT: {
    service: editAliasService,
    onRequestSuccess: function* (action: PayloadAction<EditAliasPayload>) {
      yield put(
        accountListDataActions.updateAlias({
          alias: action.payload.alias,
          accountId: action.payload.id,
        }),
      )
      yield put(accountDetailDataActions.updateAlias({ alias: action.payload.alias }))
    },
  },
}

export function* editAlias(action: PayloadAction<EditAliasPayload>) {
  try {
    const productTypeAction = action.payload?.productType
      ? editAliasActionByProductType?.[action.payload?.productType]
      : null

    if (!productTypeAction) {
      throw new Error(`No configuration for this product type: ${action.payload?.productType}`)
    }

    const apiClients: Record<Domain, ApiClient<any>> = yield getContext('apiClients')

    if (!apiClients || !apiClients['accounts']) {
      throw new Error(`API client for domain accounts not found in context`)
    }

    const api: AxiosInstance = yield call(
      apiClients['accounts'].getApi.bind(apiClients['accounts']),
    )
    const { service, onRequestSuccess } = productTypeAction

    yield put(editAliasDataActions.putRequest(action.payload))
    const response: GenericOperationState<void> = yield call(service, {
      api,
      params: action.payload,
    })

    if (!response.error) {
      yield put({
        type: editAliasDataActions.putResponse.type,
        payload: action.payload,
        error: false,
      })
      if (onRequestSuccess) {
        yield call(onRequestSuccess, action)
      }
      const closeDialog: (() => void) | undefined = yield getContext('closeDialog')
      if (closeDialog) {
        yield call(closeDialog)
      }
    }
  } catch (ex: unknown) {
    const error = ex as AxiosError<GlobalError>
    yield put({
      type: editAliasDataActions.putResponse.type,
      payload: null,
      error: true,
      meta: {
        error: error.response?.data || error.message,
        actionTypeRequest: editAliasDataActions.putRequest.type,
        httpCode: error.response?.status,
      },
    })
  }
}
